import { useState } from 'react'
import type { SocialBet } from 'src/types/domain/SocialBet'
import { BetTypeLabel } from 'src/components/BetTypeLabel/BetTypeLabel'
import type { Marble } from 'src/types/domain/Marble'
import { displayMoney } from 'src/utils/displayAmount'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGameStore, useGetCurrentLanguage, useGetCurrentGame } from 'src/store/game'
import { useSocialStore } from 'src/store/social'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
import { isMarbleGame } from 'src/types/domain/Game'
import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import { BetMarbles } from './BetMarbles'
import { getSocialCurrencySymbol } from '../../utils/getSocialCurrencySymbol'
import { getSocialUsdRate } from '../../utils/getSocialUsdRate'
import { calculateMultiplier } from '../../utils/calculateMultiplier'
import { PlayerDetails } from './PlayerDetails'

type Props = Readonly<{
  bet: SocialBet
  marbles: Marble[]
}>

export function BetListItem({ bet, marbles }: Props) {
  const [isActive, setIsActive] = useState(false)
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)
  const game = useGetCurrentGame()
  const isCrashGame = isMarbleGame(game) && isCrashGameType(game.track.gameType)

  const selectedItems =
    bet.betDetails?.selectedItems ||
    ('selected_items' in (bet.betDetails || {}) ? (bet.betDetails as any).selected_items : []) ||
    []
  const selectedMarbles = selectedItems
    .map((marbleId: string) => marbles.find((m) => m.uuid === marbleId))
    .filter((m): m is Marble => m !== undefined)

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    setIsActive(true)
  }

  const handleTouchEnd = () => {
    setIsActive(false)
  }

  const currency = useGameStore((state) => state.config?.bet?.currency)
  // An undefined server rate resolves to sweeps-table-or-1, which reproduces
  // the retired getSocialDisplayMultiplier exactly. This subtree is unmounted
  // (SocialBetPanelView only borrows PlayerDetails/BetMarbles) — keep it
  // behaviour-preserving; do NOT wire it to the store.
  const { rate: displayMultiplier, converted } = getSocialUsdRate(currency, undefined)
  const socialCurrencySymbol = getSocialCurrencySymbol(currentLanguage, currency, converted)

  // Render-time lightning-boost reversal for the social feed. The
  // bet:accepted broadcast strips the boost out of `potentialPayout`
  // for chip-placement clients pre-reveal (register_bet.go), so the
  // bet object in the store can carry the unboosted value. The
  // social store's `applyLightningBoost` action mutates these values
  // to the boosted form on reveal — but the mutation only fires on
  // store actions and can race against bet arrivals on page reload.
  // Computing the factor here at render time guarantees the display
  // is correct regardless of mutation order:
  //   - Store already boosted (`boostedBetIds[bet.id]` is true) →
  //     factor = 1, displayedPayout = bet.potentialPayout (no double-apply)
  //   - Store not yet boosted → multiply by lightning factor inline
  // Gated on `bet.raceId === currentRace.uuid` so bets from previous
  // rounds (whose lightning config we no longer track) keep their
  // stored value rather than picking up the current round's percent.
  const currentRace = isMarbleGame(game) ? game.races[0] : undefined
  const isBoostedInStore = useSocialStore((s) => Boolean(s.boostedBetIds[bet.id]))
  const lightningFactor = (() => {
    if (isBoostedInStore) return 1
    if (!currentRace) return 1
    if (bet.raceId !== currentRace.uuid) return 1
    if (!currentRace.isLightningRound) return 1
    if (!currentRace.lightningBoostedItems || currentRace.lightningBoostedItems.length === 0) {
      return 1
    }
    if (!currentRace.lightningBoostPercent || currentRace.lightningBoostPercent <= 0) return 1
    const onBoosted = selectedItems.some((m) => currentRace.lightningBoostedItems!.includes(m))
    if (!onBoosted) return 1
    return 1 + currentRace.lightningBoostPercent / 100
  })()
  const displayedPayout = bet.potentialPayout * lightningFactor

  const formattedBetDetails = {
    multiplier: calculateMultiplier(bet.betAmount, displayedPayout),
    amount: displayMoney((bet.betAmount * displayMultiplier).toString(), true),
    potentialPayout: displayMoney((displayedPayout * displayMultiplier).toString(), true)
  }

  return (
    <div
      className={`entry ${isActive ? 'touch-active' : ''}`}
      onTouchCancel={handleTouchEnd}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
    >
      <div className="bet-amount">
        <span className="amount">
          {socialCurrencySymbol}
          {formattedBetDetails.amount}
        </span>
        {!isCrashGame && <span className="multiplier">(x{formattedBetDetails.multiplier})</span>}
      </div>
      <div className="player-details">
        {!isCrashGame && (
          <span className="player-marbles">
            <BetMarbles
              bet={bet}
              selectedItems={selectedItems}
              selectedMarbles={selectedMarbles}
              simple
            />
          </span>
        )}
        <span className="spacer">
          <AngleRightIcon />
        </span>
        <span className="player-name">
          <PlayerDetails country={bet.playerCountry} name={bet.playerDisplayName} />
        </span>
      </div>
      <div className="icon">
        <AngleRightIcon />
      </div>
      <div className={`entry-hover-overlay ${bet.betDetails.type}`}>
        <div className="overlay_header">
          <span className="entry_player-name">
            <PlayerDetails country={bet.playerCountry} name={bet.playerDisplayName} />
          </span>
        </div>

        {!isCrashGame && (
          <>
            <BetTypeLabel betType={bet.betDetails.type} className="overlay_bet-type" />
            <div className="overlay_selected-marbles">
              <BetMarbles
                bet={bet}
                selectedItems={selectedItems}
                selectedMarbles={selectedMarbles}
              />
            </div>
          </>
        )}
        <div className="overlay_bet-value">
          <span className="value">
            {socialCurrencySymbol}
            {formattedBetDetails.amount}
          </span>
          <span className="label">{t('Bet Amount')}</span>
        </div>
        {!isCrashGame && (
          <div className="overlay_bet-value">
            <span className="value">
              <span>
                {socialCurrencySymbol}
                {formattedBetDetails.potentialPayout}
              </span>
              <span className="multiplier">(x{formattedBetDetails.multiplier})</span>
            </span>
            <span className="label">{t('Potential Win')}</span>
          </div>
        )}
      </div>
    </div>
  )
}
